// FIXED: PatientDetail.js - Proper Form Population and Data Handling
import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  TextField,
  LinearProgress,
  Alert,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelEditIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, Link } from "react-router-dom";
import { orderService } from "../../services/orderService";
import { useAuth } from "../../contexts/AuthContext";
import { patientService } from "../../services/patientService";
import authService from "../../services/authService";

// Date utility functions to fix timezone issues
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "N/A";

  try {
    // Parse the date string directly without creating a Date object
    // This avoids timezone conversion issues
    const dateParts = dateString.split("T")[0].split("-"); // Get YYYY-MM-DD part
    if (dateParts.length === 3) {
      const [year, month, day] = dateParts;
      return `${month}/${day}/${year}`;
    }

    // Fallback: create date in local timezone to avoid UTC conversion
    const date = new Date(dateString + "T00:00:00"); // Force local timezone
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date";
  }
};

const formatDateForInput = (dateString) => {
  if (!dateString) return "";

  try {
    // For input fields, we want YYYY-MM-DD format
    // Extract just the date part to avoid timezone issues
    const dateOnly = dateString.split("T")[0];
    return dateOnly;
  } catch (error) {
    console.error("Error formatting date for input:", error);
    return "";
  }
};

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEditPatients } = useAuth();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // FIXED: Form data state with proper initialization
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    gender: "",
    phone_number: "",
    email: "",
    address: "",
    address_notes: "",
    city: "",
    state: "",
    zip_code: "",
    medical_record_number: "",
    weight: "",
    insurance_information: "",
    alternate_contacts: [],
    emergency_contact_name: "",
    emergency_contact_phone: "",
  });

  const [originalFormData, setOriginalFormData] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (id && id !== "new") {
      fetchPatientDetails();
    } else if (id === "new") {
      // Set up for creating new patient
      setPatient(null);
      setEditing(true);
      setLoading(false);
    }
  }, [id]);

  const fetchPatientDetails = async () => {
    setLoading(true);
    setError("");

    try {

      let response;

      // Try using the patientService first
      if (patientService.getPatientById) {
        response = await patientService.getPatientById(id);
      } else {
        // Fallback to direct API call
        const token =
          sessionStorage.getItem("token") || sessionStorage.getItem("token");
        const res = await fetch(`/api/v1/patients/${id}`, {
          headers: {
            Authorization: `Bearer ${authService.getToken()}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        response = await res.json();
      }


      if (response) {
        setPatient(response);

        // FIXED: Properly populate form data with all fields
        const populatedFormData = {
          first_name: response.first_name || "",
          last_name: response.last_name || "",
          date_of_birth: response.date_of_birth
            ? formatDateForInput(response.date_of_birth)
            : "",
          gender: response.gender || "",
          phone_number: response.phone_number || "",
          email: response.email || "",
          address: response.address || "",
          address_notes: response.address_notes || "",
          city: response.city || "",
          state: response.state || "",
          zip_code: response.zip_code || "",
          medical_record_number: response.medical_record_number || "",
          weight: response.weight || "",
          insurance_information: response.insurance_info
            ? typeof response.insurance_info === "string"
              ? response.insurance_info
              : JSON.stringify(response.insurance_info, null, 2)
            : "",
          alternate_contacts: response.alternate_contacts || [],
          emergency_contact_name: response.emergency_contact_name || "",
          emergency_contact_phone: response.emergency_contact_phone || "",
        };


        setFormData(populatedFormData);
        setOriginalFormData({ ...populatedFormData });

      } else {
        throw new Error("No patient data received");
      }
    } catch (err) {
      console.error("=== ERROR FETCHING PATIENT ===");
      console.error("Error details:", err);
      setError(
        err.message.includes("404")
          ? "Patient not found"
          : `Failed to load patient details: ${err.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Required field validation
    if (!formData.first_name.trim()) {
      errors.first_name = "First name is required";
    } else if (formData.first_name.trim().length < 2) {
      errors.first_name = "First name must be at least 2 characters";
    }

    if (!formData.last_name.trim()) {
      errors.last_name = "Last name is required";
    } else if (formData.last_name.trim().length < 2) {
      errors.last_name = "Last name must be at least 2 characters";
    }

    if (!formData.address.trim()) {
      errors.address = "Address is required";
    }

    if (!formData.city.trim()) {
      errors.city = "City is required";
    }

    if (!formData.state.trim()) {
      errors.state = "State is required";
    }

    if (!formData.zip_code.trim()) {
      errors.zip_code = "ZIP code is required";
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zip_code.trim())) {
      errors.zip_code = "ZIP code must be in format 12345 or 12345-6789";
    }

    // Optional field validation
    if (
      formData.phone_number &&
      !/^\+?[\d\s\-\(\)]+$/.test(formData.phone_number)
    ) {
      errors.phone_number = "Invalid phone number format";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    if (formData.date_of_birth) {
      const birthDate = new Date(formData.date_of_birth);
      const today = new Date();
      if (birthDate > today) {
        errors.date_of_birth = "Date of birth cannot be in the future";
      }

      const age = today.getFullYear() - birthDate.getFullYear();
      if (age > 150) {
        errors.date_of_birth = "Please check the date of birth";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const hasUnsavedChanges = () => {
    const hasChanges =
      JSON.stringify(formData) !== JSON.stringify(originalFormData);
    return hasChanges;
  };

  const handleSave = async () => {

    if (!validateForm()) {
      setError("Please fix the validation errors before saving");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const updateData = { ...formData };

      // Clean and format the data
      updateData.first_name = updateData.first_name.trim();
      updateData.last_name = updateData.last_name.trim();
      updateData.address = updateData.address.trim();
      updateData.city = updateData.city.trim();
      updateData.state = updateData.state.trim();
      updateData.zip_code = updateData.zip_code.trim();

      // Handle phone number formatting
      if (updateData.phone_number) {
        updateData.phone_number = updateData.phone_number.trim();
      }

      // Handle email
      if (updateData.email) {
        updateData.email = updateData.email.trim().toLowerCase();
      }

      // Convert date to proper format
      if (updateData.date_of_birth) {
        updateData.date_of_birth = new Date(updateData.date_of_birth)
          .toISOString()
          .split("T")[0];
      }

      // Handle insurance information
      if (updateData.insurance_information) {
        try {
          // Try to parse as JSON if it looks like JSON
          if (updateData.insurance_information.trim().startsWith("{")) {
            updateData.insurance_info = JSON.parse(
              updateData.insurance_information
            );
          } else {
            updateData.insurance_info = updateData.insurance_information;
          }
        } catch (e) {
          updateData.insurance_info = updateData.insurance_information;
        }
      }

      // Remove the insurance_information field (we use insurance_info for the API)
      delete updateData.insurance_information;


      let response;

      if (id === "new") {
        // Create new patient
        if (orderService.createPatient) {
          response = await orderService.createPatient(updateData);
        } else {
          // Fallback API call
          const token =
            sessionStorage.getItem("token") || sessionStorage.getItem("token");
          const res = await fetch(`/api/v1/patients`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authService.getToken()}`,
            },
            body: JSON.stringify(updateData),
          });

          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(
              errorData.detail || `HTTP ${res.status}: ${res.statusText}`
            );
          }

          response = await res.json();
        }

        setSuccess("Patient created successfully");

        // Navigate to the new patient's detail page
        setTimeout(() => {
          navigate(`/patients/${response.id}`);
        }, 1000);
      } else {
        // Update existing patient
        if (patientService.updatePatient) {
          response = await patientService.updatePatient(id, updateData);
        } else {
          // Fallback API call
          const token =
            sessionStorage.getItem("token") || sessionStorage.getItem("token");
          const res = await fetch(`/api/v1/patients/${id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authService.getToken()}`,
            },
            body: JSON.stringify(updateData),
          });

          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(
              errorData.detail || `HTTP ${res.status}: ${res.statusText}`
            );
          }

          response = await res.json();
        }


        setPatient(response);
        setEditing(false);
        setSuccess("Patient information updated successfully");

        // FIXED: Update the form data to reflect the saved data
        const newFormData = {
          first_name: response.first_name || "",
          last_name: response.last_name || "",
          date_of_birth: response.date_of_birth
            ? formatDateForInput(response.date_of_birth)
            : "",
          gender: response.gender || "",
          phone_number: response.phone_number || "",
          email: response.email || "",
          address: response.address || "",
          address_notes: response.address_notes || "",
          city: response.city || "",
          state: response.state || "",
          zip_code: response.zip_code || "",
          medical_record_number: response.medical_record_number || "",
          weight: response.weight || "",
          insurance_information: response.insurance_info
            ? typeof response.insurance_info === "string"
              ? response.insurance_info
              : JSON.stringify(response.insurance_info, null, 2)
            : "",
          alternate_contacts: response.alternate_contacts || [],
          emergency_contact_name: response.emergency_contact_name || "",
          emergency_contact_phone: response.emergency_contact_phone || "",
        };

        setFormData(newFormData);
        setOriginalFormData({ ...newFormData });

      }
    } catch (err) {
      console.error("=== SAVE ERROR ===");
      console.error("Error saving patient:", err);

      let errorMessage = "Failed to save patient information";

      if (
        err.message.includes("duplicate") ||
        err.message.includes("already exists")
      ) {
        errorMessage = "A patient with this information already exists";
      } else if (err.message.includes("validation")) {
        errorMessage = "Please check all required fields";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges()) {
      setShowConfirmDialog(true);
    } else {
      performCancel();
    }
  };

  const performCancel = () => {
    setShowConfirmDialog(false);

    if (id === "new") {
      navigate("/patients");
    } else {
      setEditing(false);
      // FIXED: Reset form data to original patient data
      setFormData({ ...originalFormData });
      setValidationErrors({});
    }
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName];
  };

  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography>Loading patient information...</Typography>
        </Box>
      </Box>
    );
  }

  if (error && !patient && id !== "new") {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate("/patients")}>
          Back to Patients
        </Button>
      </Box>
    );
  }

  const pageTitle =
    id === "new"
      ? "Add New Patient"
      : `${patient?.first_name || ""} ${patient?.last_name || ""}`.trim() ||
        "Patient Details";

  return (
    <Box sx={{ p: isMobile ? 1.5 : 4, bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      </Snackbar>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <Button
            component={Link}
            to="/patients"
            startIcon={<BackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Patients
          </Button>
          <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
            {pageTitle}
          </Typography>
        </Box>

        <Box>
          {!editing ? (
            canEditPatients && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => setEditing(true)}
              >
                Edit Patient
              </Button>
            )
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ mr: isMobile ? 1 : 2 }}
              >
                {saving
                  ? "Saving..."
                  : id === "new"
                  ? "Create Patient"
                  : "Save Changes"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelEditIcon />}
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={isMobile ? 2 : 4}>
        <Grid  size={{ xs: 12, md: 8 }}>
          {/* Personal Information */}
          <Paper elevation={0} sx={{ p: isMobile ? 2 : 4, mb: isMobile ? 2 : 3, borderRadius: 3, border: "1px solid #E2E8F0" }}>
            <Box display="flex" alignItems="center" mb={2}>
              <PersonIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" color="primary">
                Personal Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  First Name *
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    size="small"
                    required
                    error={!!getFieldError("first_name")}
                    helperText={getFieldError("first_name")}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.first_name || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Last Name *
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    size="small"
                    required
                    error={!!getFieldError("last_name")}
                    helperText={getFieldError("last_name")}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.last_name || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Date of Birth
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={!!getFieldError("date_of_birth")}
                    helperText={getFieldError("date_of_birth")}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {formatDateForDisplay(patient?.date_of_birth)}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Gender
                </Typography>
                {editing ? (
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!getFieldError("gender")}
                  >
                    <Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                    >
                      <MenuItem value="">
                        <em>Not specified</em>
                      </MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                      <MenuItem value="Prefer not to say">
                        Prefer not to say
                      </MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.gender || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Weight
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    size="small"
                    placeholder="e.g., 180 lbs"
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.weight || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Phone Number
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    size="small"
                    placeholder="(555) 123-4567"
                    error={!!getFieldError("phone_number")}
                    helperText={getFieldError("phone_number")}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.phone_number || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Email
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    size="small"
                    placeholder="patient@example.com"
                    error={!!getFieldError("email")}
                    helperText={getFieldError("email")}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.email || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Medical Record Number
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="medical_record_number"
                    value={formData.medical_record_number}
                    onChange={handleInputChange}
                    size="small"
                    placeholder="MRN123456"
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.medical_record_number || "N/A"}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Address Information */}
          <Paper elevation={0} sx={{ p: isMobile ? 2 : 4, mb: isMobile ? 2 : 3, borderRadius: 3, border: "1px solid #E2E8F0" }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Address Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid  size={{ xs: 12 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Address *
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    size="small"
                    required
                    placeholder="123 Main Street"
                    error={!!getFieldError("address")}
                    helperText={getFieldError("address")}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.address || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Address Notes
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="address_notes"
                    value={formData.address_notes}
                    onChange={handleInputChange}
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Apartment number, gate code, special instructions..."
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.address_notes || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12, sm: 4 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  City *
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    size="small"
                    required
                    placeholder="Los Angeles"
                    error={!!getFieldError("city")}
                    helperText={getFieldError("city")}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.city || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12, sm: 4 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  State *
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    size="small"
                    required
                    placeholder="CA"
                    error={!!getFieldError("state")}
                    helperText={getFieldError("state")}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.state || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12, sm: 4 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  ZIP Code *
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                    size="small"
                    required
                    placeholder="90210"
                    error={!!getFieldError("zip_code")}
                    helperText={getFieldError("zip_code")}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {patient?.zip_code || "N/A"}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid  size={{ xs: 12, md: 4 }}>
          {/* Additional Information */}
          <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, mb: isMobile ? 2 : 3, borderRadius: 3, border: "1px solid #E2E8F0" }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid  size={{ xs: 12 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Insurance Information
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="insurance_information"
                    value={formData.insurance_information}
                    onChange={handleInputChange}
                    size="small"
                    multiline
                    rows={4}
                    placeholder="Insurance company, policy number, group number..."
                  />
                ) : (
                  <Typography variant="body2" gutterBottom>
                    {patient?.insurance_info
                      ? typeof patient.insurance_info === "string"
                        ? patient.insurance_info
                        : JSON.stringify(patient.insurance_info, null, 2)
                      : "N/A"}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Emergency Contact */}
          <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, borderRadius: 3, border: "1px solid #E2E8F0" }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Emergency Contact
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid  size={{ xs: 12 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Contact Name
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="emergency_contact_name"
                    value={formData.emergency_contact_name}
                    onChange={handleInputChange}
                    size="small"
                    placeholder="Emergency contact name"
                  />
                ) : (
                  <Typography variant="body2" gutterBottom>
                    {patient?.emergency_contact_name || "N/A"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Contact Phone
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="emergency_contact_phone"
                    value={formData.emergency_contact_phone}
                    onChange={handleInputChange}
                    size="small"
                    placeholder="(555) 123-4567"
                  />
                ) : (
                  <Typography variant="body2" gutterBottom>
                    {patient?.emergency_contact_phone || "N/A"}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Alternate Contacts */}
          <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, borderRadius: 3, border: "1px solid #E2E8F0" }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Alternate Contact Numbers
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {editing ? (
              <>
                {(formData.alternate_contacts || []).map((contact, index) => (
                  <Grid container spacing={1} key={index} sx={{ mb: 1.5 }}>
                    <Grid  size={{ xs: 12, sm: 5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Contact Name"
                        value={contact.name || ""}
                        onChange={(e) => {
                          const updated = [...(formData.alternate_contacts || [])];
                          updated[index] = { ...updated[index], name: e.target.value };
                          setFormData((prev) => ({ ...prev, alternate_contacts: updated }));
                        }}
                        placeholder="e.g., Wife - Maria"
                      />
                    </Grid>
                    <Grid  size={{ xs: 10, sm: 5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Phone Number"
                        value={contact.phone || ""}
                        onChange={(e) => {
                          const updated = [...(formData.alternate_contacts || [])];
                          updated[index] = { ...updated[index], phone: e.target.value };
                          setFormData((prev) => ({ ...prev, alternate_contacts: updated }));
                        }}
                        placeholder="(555) 123-4567"
                      />
                    </Grid>
                    <Grid  size={{ xs: 2, sm: 2 }}>
                      <IconButton
                        color="error"
                        onClick={() => {
                          const updated = (formData.alternate_contacts || []).filter(
                            (_, i) => i !== index
                          );
                          setFormData((prev) => ({ ...prev, alternate_contacts: updated }));
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      alternate_contacts: [
                        ...(prev.alternate_contacts || []),
                        { name: "", phone: "" },
                      ],
                    }));
                  }}
                >
                  Add Alternate Contact
                </Button>
              </>
            ) : (
              <>
                {patient?.alternate_contacts && patient.alternate_contacts.length > 0 ? (
                  patient.alternate_contacts.map((contact, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        <strong>{contact.name || "Contact"}:</strong>{" "}
                        {contact.phone || "N/A"}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No alternate contacts
                  </Typography>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to cancel without
            saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Continue Editing
          </Button>
          <Button onClick={performCancel} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientDetail;
